<template>
    <v-container fluid class="page-container">
        <page-header title="Members" :subtitle="totalLabel">
            <template v-if="isAdmin">
                <v-btn outlined color="primary" :loading="exporting" @click="exportMembers">
                    <v-icon left>mdi-file-delimited-outline</v-icon>Export CSV
                </v-btn>
                <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn outlined color="primary" v-bind="attrs" v-on="on">
                            More<v-icon right>mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item @click="importDialog = true">
                            <v-list-item-icon><v-icon>mdi-upload</v-icon></v-list-item-icon>
                            <v-list-item-title>Import from CSV</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="downloadContacts('phones')">
                            <v-list-item-icon><v-icon>mdi-phone-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>Newsletter phones (CSV)</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="downloadContacts('emails')">
                            <v-list-item-icon><v-icon>mdi-email-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>Newsletter emails (CSV)</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-btn color="primary" depressed @click="createItem">
                    <v-icon left>mdi-plus</v-icon>New member
                </v-btn>
            </template>
        </page-header>

        <filter-panel
            :fields="filterFields"
            :value="listState"
            :loading="loading"
            @apply="applyFilters"
        />

        <v-card outlined>
            <v-data-table
                :headers="visibleHeaders"
                v-bind="tableBindings"
                class="list-table clickable-rows"
                no-data-text="No members match these filters"
                @update:options="onTableOptions"
                @click:row="openItem($event)"
            >
                <template v-slot:[`item.name`]="{ item }">
                    <span class="font-weight-medium">{{ item.firstName }} {{ item.lastName }}</span>
                </template>
                <template v-slot:[`item.paronCard`]="{ item }">{{ cardsOf(item, 'PARON') }}</template>
                <template v-slot:[`item.myMarketCard`]="{ item }">{{ cardsOf(item, 'My Market') }}</template>
                <template v-slot:[`item.abCard`]="{ item }">{{ cardsOf(item, 'AB') }}</template>
                <template v-slot:[`item.flags`]="{ item }">
                    <v-chip v-if="item.vip" x-small color="amber lighten-4" class="mr-1">VIP</v-chip>
                    <v-chip v-if="item.exclusive" x-small color="purple lighten-4" class="mr-1">Exclusive</v-chip>
                    <v-chip v-if="item.reliable" x-small color="green lighten-4" class="mr-1">Reliable</v-chip>
                </template>
                <template v-slot:[`item.team`]="{ item }">{{ item.team ? item.team.title : '' }}</template>
                <template v-slot:[`item.subscriptionDate`]="{ item }">{{ formatDate(item.subscriptionDate) }}</template>
                <template v-slot:[`item.approved`]="{ item }">
                    <v-chip
                        small
                        :outlined="!item.approved"
                        :color="item.approved ? 'success' : 'error'"
                        :disabled="togglingId === item.id"
                        :title="isAdmin ? 'Click to toggle' : ''"
                        @click.stop="isAdmin && toggleApprove(item)"
                    >
                        {{ item.approved ? 'Approved' : 'Pending' }}
                    </v-chip>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex justify-end">
                        <v-btn icon small color="primary" title="Edit" @click.stop="openItem(item, 'edit')">
                            <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                        <delete-button
                            v-if="isAdmin"
                            title="Delete this member?"
                            text="The member and their data will be permanently removed."
                            @delete-entry="deleteItem(item, deleteMember, 'Member')"
                        />
                    </div>
                </template>
            </v-data-table>
            <table-footer v-model="page" :page-count="pageCount" :total="total" :items-per-page="itemsPerPage" />
        </v-card>

        <member-import-dialog v-model="importDialog" @imported="fetchItems" />
    </v-container>
</template>

<script>
    import memberService from '@/services/member-service'
    import dealerService from '@/services/dealer-service'
    import paginatedList from '@/mixins/paginated-list'
    import PageHeader from '@/components/PageHeader'
    import FilterPanel from '@/components/FilterPanel'
    import TableFooter from '@/components/TableFooter'
    import DeleteButton from '@/components/DeleteButton'
    import MemberImportDialog from '@/components/MemberImportDialog'
    import { activeCardNumbers, formatDate, fullName, saveBlob, dateStamp } from '@/utils/format'

    export default {
        name: 'MemberRoute',
        components: { PageHeader, FilterPanel, TableFooter, DeleteButton, MemberImportDialog },
        mixins: [paginatedList],
        data() {
            return {
                scopeToDealer: true,
                dealers: null,
                togglingId: null,
                exporting: false,
                importDialog: false,
                headers: [
                    { text: 'Number', value: 'number' },
                    { text: 'Name', value: 'name' },
                    { text: 'Address', value: 'address' },
                    { text: 'PARON card', value: 'paronCard' },
                    { text: 'My Market card', value: 'myMarketCard' },
                    { text: 'AB card', value: 'abCard' },
                    { text: 'Category', value: 'flags' },
                    { text: 'Team', value: 'team' },
                    { text: 'Recommendation', value: 'recommendation' },
                    { text: 'Αριθμός Δημοτολογίου', value: 'dimotologioNumber' },
                    { text: 'Subscribed', value: 'subscriptionDate' },
                    { text: 'Status', value: 'approved' },
                    { text: '', value: 'actions', align: 'end', sortable: false, width: 96 }
                ]
            }
        },
        computed: {
            filterFields() {
                const fields = [
                    { key: 'member', label: 'Name', placeholder: 'First or last name' },
                    { key: 'number', label: 'Member number', type: 'number' },
                    { key: 'card', label: 'Card number' },
                    { key: 'vip', label: 'VIP', type: 'boolean' },
                    { key: 'exclusive', label: 'Exclusive', type: 'boolean' },
                    { key: 'reliable', label: 'Reliable', type: 'boolean' },
                    { key: 'approved', label: 'Approved', type: 'boolean' }
                ]
                if (this.isAdmin) {
                    fields.push({ key: 'dealer', label: 'Dealer', type: 'autocomplete', items: this.dealers, itemText: fullName })
                }
                return fields
            }
        },
        created() {
            if (this.isAdmin) {
                dealerService.getAllDealers().then(resp => {
                    this.dealers = resp.data.data
                }).catch(() => {
                    this.dealers = []
                })
            }
        },
        methods: {
            formatDate,
            fetchPage({ page, perPage, filters, sortOption, order }) {
                return memberService.getMembersByFilter(page, filters, sortOption, order, perPage)
            },
            deleteMember: id => memberService.deleteMemberById(id),
            cardsOf(member, type) {
                return activeCardNumbers(member.cards, type).join(', ')
            },
            toggleApprove(member) {
                this.togglingId = member.id
                memberService.toggleApproved(member.id).then(() => {
                    member.approved = !member.approved
                    this.$notify.success(`${fullName(member)} is now ${member.approved ? 'approved' : 'pending'}`)
                }).catch(error => {
                    this.$notify.error(error, 'Could not change the approval')
                }).finally(() => {
                    this.togglingId = null
                })
            },
            exportMembers() {
                this.exporting = true
                memberService.exportMembers(this.requestFilters(), this.sortOption, this.order).then(resp => {
                    saveBlob(resp.data, `members_${dateStamp()}.csv`)
                }).catch(error => {
                    this.$notify.error(error, 'Could not export the members')
                }).finally(() => {
                    this.exporting = false
                })
            },
            downloadContacts(kind) {
                const request = kind === 'phones' ? memberService.getMembersPhones() : memberService.getMembersEmails()
                request.then(resp => {
                    saveBlob(resp.data, `${kind}_${dateStamp()}.csv`)
                }).catch(error => {
                    this.$notify.error(error, `Could not download the ${kind}`)
                })
            }
        }
    }
</script>
